// Resolves lender/bank logo image URLs from appConfig/main.
//
// appConfig/main carries a master bank list whose entries point at logo
// images uploaded to Firebase Storage (storage bucket path bank_logos/...),
// but the exact field name/shape isn't fixed by any code in this repo (it
// was authored via the Firebase console). Rather than hardcode a field
// name, this scans every top-level field on the document and picks out
// whichever shape looks like a bank -> logo-URL mapping:
//
//   - an array of objects shaped like
//     { name|bank|label|key: string, logoUrl|logo|url|imageUrl: string }
//   - a plain object map of { [bankName]: url }
//
// Matching entries are merged into a single map, keyed by a normalized
// (trimmed, lowercased) bank name for lookups.

export type BankLogoMap = Record<string, string>;

const isUrlLike = (s: string) => {
  const value = s.trim();
  if (!value) return false;
  if (/^gs:\/\//i.test(value)) return true;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const norm = (s: string) => s.trim().toLowerCase();

export function extractBankLogoMap(mainDocData: Record<string, unknown> | undefined): BankLogoMap {
  if (!mainDocData) return {};
  const map: BankLogoMap = {};

  for (const value of Object.values(mainDocData)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        if (!item || typeof item !== 'object') continue;
        const obj = item as Record<string, unknown>;
        let name: string | undefined;
        let url: string | undefined;
        for (const [k, v] of Object.entries(obj)) {
          if (typeof v !== 'string') continue;
          const lk = k.toLowerCase();
          if (!url && (lk.includes('logo') || lk.includes('url') || lk.includes('image')) && isUrlLike(v)) {
            url = v;
          } else if (!name && (lk.includes('name') || lk.includes('bank') || lk === 'key' || lk === 'label')) {
            name = v;
          }
        }
        if (name?.trim() && url) map[norm(name)] = url;
      }
    } else if (value && typeof value === 'object') {
      for (const [topKey, topVal] of Object.entries(value as Record<string, unknown>)) {
        if (typeof topVal === 'string' && isUrlLike(topVal) && topKey.trim()) {
          // map of { [bankName]: url }
          map[norm(topKey)] = topVal;
        } else if (topVal && typeof topVal === 'object' && !Array.isArray(topVal)) {
          // map of { [bankName]: { name?, logourl?, ... } }
          const obj = topVal as Record<string, unknown>;
          let name: string | undefined;
          let url: string | undefined;
          for (const [k, v] of Object.entries(obj)) {
            if (typeof v !== 'string') continue;
            const lk = k.toLowerCase();
            if (!url && (lk.includes('logo') || lk.includes('url') || lk.includes('image')) && isUrlLike(v)) {
              url = v;
            } else if (!name && (lk.includes('name') || lk.includes('bank') || lk === 'key' || lk === 'label')) {
              name = v;
            }
          }
          if (url) map[norm(name?.trim() || topKey)] = url;
        }
      }
    }
  }

  return map;
}

/** Look up a logo URL for `lenderName`, trying exact then fuzzy (prefix) match. */
export function getBankLogoUrl(map: BankLogoMap, lenderName: string): string | null {
  if (!lenderName) return null;
  const lower = norm(lenderName);
  if (map[lower]) return map[lower];
  for (const [key, url] of Object.entries(map)) {
    if (key.startsWith(lower) || lower.startsWith(key)) return url;
  }
  return null;
}
